import React, {useEffect, useMemo, useState} from 'react';

import {createListCollection, Portal, Select, Text, VStack} from "@chakra-ui/react";
import {CirclePlus} from "lucide-react";

import {Field, AddNumber, AddChoice, AddText, Button} from "@/components";
import {useModal} from "@/contexts/modalContext";
import FormBuilder, {fieldCorresponding} from "./FormBuilder";
import EntityModalContent from "@/components/entity/EntityModalContent";
import {useLanguage} from "@/contexts/languageContext";

export const CreateOrEditForm = ({onChange, fields}) => {

    const {isOpen, open, close, updateContent} = useModal();
    const [currentType, setCurrentType] = useState(null);
    const { availableLanguages, t } = useLanguage();

    const Body = useMemo(() => {
        switch (currentType) {
            case 'select':
            case 'checkbox':
            case 'radio':
                return <AddChoice key={`body-${currentType}`} fields={fields} type={currentType}/>;
            case 'number':
                return <AddNumber key={`body-${currentType}`} fields={fields} type={currentType}/>;
            case 'text':
                return <AddText key={`body-${currentType}`} fields={fields} type={currentType}/>;
            default:
                return null;
        }

    }, [currentType]);

    useEffect(() => {
        if (isOpen('addFormField') || (Body !== null)) {
            updateContent(modalContent);
        }
    }, [currentType]);

    const onClose = () => {
        setCurrentType(_ => null);
        close();
    }

    const handleAddField = () => {
        document.dispatchEvent(new CustomEvent('addField', {
            bubbles: true,
            detail: {
                onChange: (field) => {
                    const newValue = availableLanguages.reduce((acc, lang) => {
                        return {
                            ...acc,
                            [lang]: [...fields[lang], field[lang]]
                        }
                    }, {});
                    onChange(newValue)
                },
                onClose
            }
        }));
    }

    const modalContent = useMemo(() => {
        const collection = createListCollection({
            items: Object.keys(fieldCorresponding).map((key) => ({
                label: t(fieldCorresponding[key].name), value: key
            }))
        })
        return <EntityModalContent
            title={t('sentences.addAField')}
            close={close}
            Body={<VStack p="1.5rem" overflowY="hidden" w="100%">
                <Field
                    label={t('wrappers.nonMandatoryLabel', { value: t('fields.fieldType') })}
                    p={4}
                >
                    <Select.Root
                        onValueChange={(e) => setCurrentType(_ => e?.value?.[0] ?? '')}
                        collection={collection}
                        defaultValue={currentType ?? ''}
                    >
                        <Select.HiddenSelect/>
                        <Select.Control>
                            <Select.Trigger>
                                <Select.ValueText placeholder={t('sentences.chooseFieldType')}/>
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator/>
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                            <Select.Positioner>
                                <Select.Content>
                                    {collection.items.map((item) => (
                                        <Select.Item item={item} key={`select-item-${item.value}`}>
                                            {item.label}
                                            <Select.ItemIndicator/>
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Portal>
                    </Select.Root>
                </Field>
                {Body}
                <Button
                    variant="primary"
                    mt="auto"
                    height="auto"
                    borderRadius=".5rem"
                    style={{padding: "1rem 1.5rem"}}
                    onClick={() => currentType !== null ? handleAddField() : close()}
                >
                    {currentType !== null ? t('actions.add') : t('actions.close')}
                </Button>
            </VStack>}
        />
    }, [Body])

    const openModal = () => {
        open(modalContent, 'cover', {}, true, 'addFormField');
    }

    return (
        <VStack w="100%" h="100%" overflowY="hidden" gap="2vh">
            {(fields?.length ?? -1) === 0 &&
                <Text my="3vh" w="100%" color="primary.400" fontSize="large" textAlign="center">{t('sentences.noEntityYet.question')}</Text>}
            <Button
                onClick={openModal}
                rightIcon={<CirclePlus/>}
            >
                {t('sentences.addAField')}
            </Button>
            <VStack bg="primary.200" borderRadius="1rem" w="100%" borderWidth=".15rem" p="1.5rem">
                {(fields?.length ?? 0) > 0 && <Text fontSize="2xl">{t('preview')}</Text>}
                <FormBuilder
                    canEdit={true}
                    fields={fields}
                    onEdit={onChange}
                    flex={1}
                    overflowY="auto"
                    w="100%"
                />
            </VStack>
        </VStack>
    );
};

export default CreateOrEditForm;
